import {
  Bars3Icon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import clsx from "clsx";
import { formatDistanceToNow } from "date-fns";
import mixpanel from "mixpanel-browser";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  EnumReferralPendingAction,
  RecentActivityItem,
} from "../__generated__/graphql";
import HomeChatInput from "../components/Chat/HomeChatInput";
import GradingPalHomeBanner from "../components/GradingPal/GradingPalHomeBanner";
import AccountDropdown from "../components/Dropdowns/AccountDropdown";
import ActivityDropdown from "../components/Dropdowns/ActivityDropdown";
import { useToastsContext } from "../contexts/toasts";
import useIsMobile from "../hooks/useIsMobile";
import useStores from "../hooks/useStores";
import {
  AppsaddIcon,
  Book01Icon,
  ChatIcon,
  CheckmarkcircleIcon,
  File03Icon,
  HomeIcon,
  ImageIcon,
  MailIcon,
  MonitorIcon,
  PresentationIcon,
  Wallet01Icon,
} from "../icon-imports";
import { PricingPlanTier } from "../models/User";
import {
  activeToolRoute,
  conversationRoute,
  editResourceRoute,
  imageGeneratorOutputRoute,
  instantResourcesOutputRoute,
  tasksRoute,
} from "../utils/routeHelper";
import { isPlanLimitError } from "../utils/planLimit";

// Helper function to get activity URL
const getActivityUrl = (type: string, url: string) => {
  switch (type) {
    case "Chat":
      return { path: conversationRoute(url) };
    case "InstantResources":
      return { path: instantResourcesOutputRoute(url) };
    case "CurriculumCreator":
      return { path: activeToolRoute(url) };
    case "ImageGenerator":
      return { path: imageGeneratorOutputRoute(url) };
    case "Slides":
      return { path: url, external: true };
    case "Documents":
      return { path: editResourceRoute(url) };
    default:
      return { path: url };
  }
};

// Helper function to get icon and color based on activity type
const getActivityMeta = (type: string) => {
  switch (type) {
    case "Chat":
      return { icon: ChatIcon, color: "sky" };
    case "InstantResources":
      return { icon: AppsaddIcon, color: "purple" };
    case "CurriculumCreator":
      return { icon: Book01Icon, color: "red" };
    case "ImageGenerator":
      return { icon: ImageIcon, color: "yellow" };
    case "Slides":
      return { icon: MonitorIcon, color: "orange" };
    case "Documents":
      return { icon: File03Icon, color: "red" };
    default:
      return { icon: File03Icon, color: "slate" };
  }
};

// Helper function to get friendly name for activity type (shown in "Continue working")
const getActivityTypeName = (type: string) => {
  switch (type) {
    case "Chat":
      // Previously "Chat with me"
      return "Chat";
    case "InstantResources":
      // Previously "Bulk Teaching Materials" / "Differentiate It"
      return "Materials";
    case "CurriculumCreator":
      return "Curriculum Studio";
    case "ImageGenerator":
      // Previously "Create or Edit an Image"
      return "Image";
    case "Slides":
      return "Create Slides";
    case "Documents":
      return "Document";
    default:
      return "Resource";
  }
};

function extractTextFromHtml(html: string) {
  // Create a new DOM parser
  const parser = new DOMParser();

  // Parse the HTML string to a document
  const doc = parser.parseFromString(html, "text/html");

  // Extract the text content from the document
  return doc.body.textContent || "";
}

// Array of greeting messages that rotate randomly
const greetingMessages = [
  "What's on your mind?",
  "What's on the agenda today?",
  "Where should we begin?",
  "How can I help?",
  "Hi {firstname}, good to see you!",
  "Ready when you are",
  "What are you working on?",
  "What should we tackle today?",
  "Hey {firstname}, Ready to dive in?",
];

// Get a random greeting message and replace {firstname} with actual first name
const getGreetingMessage = (firstName?: string | null) => {
  const randomIndex = Math.floor(Math.random() * greetingMessages.length);
  let message = greetingMessages[randomIndex];

  // Replace {firstname} with actual first name if provided, otherwise use a generic greeting
  if (firstName) {
    message = message.replace(/\{firstname\}/gi, firstName);
  } else {
    // If no first name, replace with generic alternatives
    message = message.replace(/\{firstname\}/gi, "");
    // Clean up any double spaces or leading/trailing commas
    message = message.replace(/\s+/g, " ").replace(/,\s*,/g, ",").trim();
    // Fix cases where we removed the name but left "Hi ," or "Hey ,"
    message = message.replace(/^(Hi|Hey)\s*,\s*/i, "");
  }

  return message;
};

const getActivityIconClasses = (color: string) => {
  return clsx("shrink-0 w-8 h-8 rounded-lg flex items-center justify-center", {
    "bg-sky-100": color === "sky",
    "bg-green-100": color === "green",
    "bg-yellow-100": color === "yellow",
    "bg-red-100": color === "red",
    "bg-orange-100": color === "orange",
    "bg-purple-100": color === "purple",
  });
};

const getActivityIconColorClasses = (color: string) => {
  return clsx("w-4 h-4", {
    "text-sky-600": color === "sky",
    "text-green-600": color === "green",
    "text-yellow-600": color === "yellow",
    "text-red-600": color === "red",
    "text-orange-600": color === "orange",
    "text-purple-600": color === "purple",
  });
};

// Activity list item component - defined outside Home to prevent re-creation on every render
const ActivityListItem = ({ activity }: { activity: RecentActivityItem }) => {
  const { icon: ActivityIcon, color } = getActivityMeta(activity.type);
  const typeName = getActivityTypeName(activity.type);
  const { path } = getActivityUrl(activity.type, activity.url);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  return (
    <div className="relative group">
      <Link
        to={path}
        data-activity-type={activity.type}
        data-activity-title={activity.title}
        className="activity-link flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors group"
      >
        <div className={getActivityIconClasses(color)}>
          <ActivityIcon className={getActivityIconColorClasses(color)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {activity.title}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {formatDistanceToNow(new Date(activity.updatedAt), {
              addSuffix: true,
            })}
            {" "}• {typeName}
          </p>
        </div>
      </Link>
      <div
        className={clsx(
          "absolute top-2 right-2 z-10 transition-opacity",
          isDropdownOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        )}
      >
        <ActivityDropdown
          activityId={activity.id}
          activityType={activity.type}
          onOpenChange={setIsDropdownOpen}
        />
      </div>
    </div>
  );
};

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, conversations, auditLogs, conversationParticipants } =
    useStores();
  const { addToast } = useToastsContext();
  const isMobile = useIsMobile();
  const [selectedMode, setSelectedMode] = React.useState("chat");
  const [text, setText] = React.useState("");
  const [assetIds, setAssetIds] = React.useState<string[]>([]);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);

  // Load sidebar state from localStorage (only for desktop)
  // COMMENTED OUT: Sidebar collapse functionality disabled for desktop
  // const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(() => {
  //   const saved = localStorage.getItem("sidebarCollapsed");
  //   return saved === "true";
  // });
  // const [isHoveringSidebar, setIsHoveringSidebar] = React.useState(false);
  // const isSidebarCollapsed = false; // Always expanded

  // Save sidebar state to localStorage when it changes
  // COMMENTED OUT: Sidebar collapse functionality disabled for desktop
  // React.useEffect(() => {
  //   localStorage.setItem("sidebarCollapsed", String(isSidebarCollapsed));
  // }, [isSidebarCollapsed]);

  // Get a random greeting message with user's first name
  const greetingMessage = React.useMemo(() => {
    return getGreetingMessage(auth.user?.firstName);
  }, [auth.user?.firstName]);

  // Memoize the text change handler to prevent recreation
  const handleTextChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setText(e.target.value);
    },
    []
  );

  // const myDriveId = React.useMemo(() => {
  //   const myDrive = drives.sortedData.find((drive) => {
  //     return drive.driveType === EnumDriveDriveType.MyDrive;
  //   });

  //   if (!myDrive) return null;

  //   return myDrive.id;
  // }, [drives.sortedData]);

  if (!auth.user) return null;

  const currentPlan = auth.user.currentPlan;
  const recentActivities = toJS(auth.recentActivities);
  const { chatFileUploadUsesLeft } = auditLogs;

  // Opens the Stripe customer portal, where subscribers can cancel, switch plans,
  // update their card and download invoices
  const handleManageSubscription = async () => {
    if (auth.isRedirectingToStripeCustomerPortal) return;

    const url = await auth.redirectToStripeCustomerPortal();
    if (url) {
      window.location.href = url;
    } else {
      addToast(
        "We couldn't open your billing portal. Please email support@alayna.us and we'll help right away.",
        { type: "error" }
      );
    }
  };

  const renderManageSubscriptionButton = () => {
    if (!auth.user?.hasPersonalSubscription) return null;

    return (
      <button
        type="button"
        onClick={handleManageSubscription}
        disabled={auth.isRedirectingToStripeCustomerPortal}
        className="flex items-center gap-3 w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-60"
      >
        <Wallet01Icon className="w-4 h-4 text-black" />
        {auth.isRedirectingToStripeCustomerPortal
          ? "Opening billing portal..."
          : "Manage Subscription"}
      </button>
    );
  };

  // Fetch recent activities on component mount
  React.useEffect(() => {
    auth.getRecentActivityItems();
  }, [auth]);

  // Set up analytics tracking for activity links
  React.useEffect(() => {
    mixpanel.track_links(
      ".activity-link",
      "activity-clicked",
      (el: HTMLElement) => ({
        activity_type: el.getAttribute("data-activity-type"),
        activity_title: el.getAttribute("data-activity-title"),
      })
    );
  }, [recentActivities]);

  // Refs for infinite scroll (desktop)
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Refs for infinite scroll (mobile)
  const mobileSentinelRef = React.useRef<HTMLDivElement>(null);
  const mobileScrollContainerRef = React.useRef<HTMLDivElement>(null);

  /**
   * Infinite scroll observer
   * Watches for the sentinel element to become visible and triggers loading more items.
   * Skips the initial trigger to prevent loading on mount when sentinel is already visible.
   */
  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (!sentinel || !scrollContainer) return;

    let isInitialTrigger = true;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        // Skip the initial trigger when observer first attaches
        if (isInitialTrigger) {
          isInitialTrigger = false;
          return;
        }

        const canLoadMore =
          entry.isIntersecting &&
          auth.hasMoreRecentActivities &&
          !auth.isFetchingMoreRecentActivities &&
          !auth.isFetchingRecentActivities;

        if (canLoadMore) {
          auth.loadMoreRecentActivities();
        }
      },
      {
        root: scrollContainer,
        rootMargin: "100px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [auth, auth.hasMoreRecentActivities, auth.isFetchingRecentActivities]);

  /**
   * Auto-fill when content is too short (desktop)
   * After deletions, if the remaining content doesn't fill the scroll container,
   * automatically load more items to ensure the user can continue scrolling.
   */
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || recentActivities.length === 0) return;

    const checkAndLoadMore = () => {
      const contentTooShort =
        scrollContainer.scrollHeight <= scrollContainer.clientHeight;
      const canLoadMore =
        auth.hasMoreRecentActivities &&
        !auth.isFetchingMoreRecentActivities &&
        !auth.isFetchingRecentActivities;

      if (contentTooShort && canLoadMore) {
        auth.loadMoreRecentActivities();
      }
    };

    // Delay to allow DOM to update after deletions
    const timeoutId = setTimeout(checkAndLoadMore, 100);
    return () => clearTimeout(timeoutId);
  }, [recentActivities.length, auth]);

  /**
   * Mobile infinite scroll observer
   * Same as desktop but for the mobile sidebar scroll container.
   */
  React.useEffect(() => {
    const sentinel = mobileSentinelRef.current;
    const scrollContainer = mobileScrollContainerRef.current;

    if (!sentinel || !scrollContainer || !isMobileSidebarOpen) return;

    let isInitialTrigger = true;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        // Skip the initial trigger when observer first attaches
        if (isInitialTrigger) {
          isInitialTrigger = false;
          return;
        }

        const canLoadMore =
          entry.isIntersecting &&
          auth.hasMoreRecentActivities &&
          !auth.isFetchingMoreRecentActivities &&
          !auth.isFetchingRecentActivities;

        if (canLoadMore) {
          auth.loadMoreRecentActivities();
        }
      },
      {
        root: scrollContainer,
        rootMargin: "100px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [
    auth,
    auth.hasMoreRecentActivities,
    auth.isFetchingRecentActivities,
    isMobileSidebarOpen,
  ]);

  const isHomeActive = location.pathname === "/";
  // const isWorkspaceActive = location.pathname.startsWith("/drive/");

  return (
    <div className="flex flex-col h-screen bg-[#fefefe] overflow-hidden">
      {/* Mobile Navbar */}
      {isMobile && (
        <div className="flex-shrink-0 w-full bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open menu"
          >
            <Bars3Icon className="w-6 h-6 text-gray-700" />
          </button>
          <Link
            to="/"
            className="block hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img
              src="/imgs/logos/gray-logo.png"
              alt="Alayna AI Logo"
              className="w-8 h-8"
            />
          </Link>
          <div className="flex-shrink-0">
            <AccountDropdown>
              <button className="flex items-center gap-2">
                {auth.user?.avatarUrl ? (
                  <img
                    src={auth.user.avatarUrl}
                    alt="User Avatar"
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium text-xs">
                    {auth.user?.firstName?.charAt(0)}
                    {auth.user?.lastName?.charAt(0)}
                  </div>
                )}
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              </button>
            </AccountDropdown>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Drawer */}
      {isMobile && (
        <>
          {/* Overlay */}
          <div
            className={clsx(
              "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300",
              isMobileSidebarOpen
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            )}
            onClick={() => setIsMobileSidebarOpen(false)}
          />

          {/* Sidebar Drawer */}
          <div
            className={clsx(
              "fixed inset-y-0 left-0 w-72 bg-[#fafafa] z-50 flex flex-col transition-transform duration-300 ease-in-out",
              isMobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            {/* Header with close button */}
            <div className="p-4 flex items-center justify-between border-b border-gray-200">
              <Link
                to="/"
                className="block hover:opacity-80 transition-opacity"
                onClick={() => setIsMobileSidebarOpen(false)}
              >
                <img
                  src="/imgs/logos/gray-logo.png"
                  alt="Alayna AI Logo"
                  className="w-8 h-8"
                />
              </Link>
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Primary Navigation */}
            <div className="p-4 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMobileSidebarOpen(false)}
                className={clsx(
                  "flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                  isHomeActive
                    ? "bg-[#eaeaea] text-gray-900"
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                <HomeIcon className="w-4 h-4 text-black" />
                Home
              </Link>
            </div>

            {/* Continue working Section */}
            <div
              ref={mobileScrollContainerRef}
              className="flex-1 overflow-y-auto px-4 pb-4"
            >
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2">
                Continue working
              </h3>
              <div className="space-y-1">
                {auth.isFetchingRecentActivities ? (
                  <div className="space-y-2 px-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse" />
                        <div className="flex-1">
                          <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse mb-1" />
                          <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : auth.errorFetchingRecentActivities ? (
                  <div className="px-2 py-4 text-sm text-red-600 text-center">
                    Failed to load activities
                  </div>
                ) : recentActivities.length === 0 ? (
                  <div className="px-2 py-4 text-sm text-gray-500 text-center">
                    No recent activities
                  </div>
                ) : (
                  <>
                    {recentActivities.map((activity) => (
                      <div
                        key={activity.id}
                        onClick={() => setIsMobileSidebarOpen(false)}
                      >
                        <ActivityListItem activity={activity} />
                      </div>
                    ))}
                    {/* Sentinel for infinite scroll (mobile) */}
                    {auth.hasMoreRecentActivities && (
                      <div ref={mobileSentinelRef} className="py-2">
                        {auth.isFetchingMoreRecentActivities && (
                          <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
                            <div className="w-3 h-3 border border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            Loading more...
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 space-y-1">
              {renderManageSubscriptionButton()}
              <a
                href="https://www.canva.com/design/DAF5XKzIa_o/PLPm0juXs0bBBT_LQCBSSQ/edit?utm_content=DAF5XKzIa_o&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <PresentationIcon className="w-4 h-4 text-black" />
                PD Deck
              </a>
              <a
                href="mailto:hello@alayna.ai?subject=Bring Alayna to Your School"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MailIcon className="w-4 h-4 text-black" />
                Bring Alayna to Your School
              </a>

              {/* Profile Info */}
              <div className="flex items-center gap-3 px-4 py-2 mt-2">
                {auth.user.avatarUrl ? (
                  <img
                    src={auth.user.avatarUrl}
                    alt="User Avatar"
                    className="w-7 h-7 rounded-full"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium text-xs">
                    {auth.user?.firstName?.charAt(0)}
                    {auth.user?.lastName?.charAt(0)}
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {auth.user.firstName} {auth.user.lastName}
                  </p>
                  {currentPlan !== PricingPlanTier.Basic && (
                    <p className="text-xs text-pink-600">PRO</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Desktop Only */}
        {!isMobile && (
          <div
            className={clsx(
              "bg-[#fafafa] border-r border-gray-200 flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out",
              // COMMENTED OUT: Sidebar always expanded
              // isSidebarCollapsed ? "w-16" : "w-64"
              "w-64"
            )}
            // COMMENTED OUT: Sidebar collapse functionality disabled
            // onMouseEnter={() => setIsHoveringSidebar(true)}
            // onMouseLeave={() => setIsHoveringSidebar(false)}
          >
            {/* Top Section - Logo */}
            <div className="p-4 pt-8 flex items-center justify-between">
              {/* COMMENTED OUT: Collapse/expand functionality disabled for desktop */}
              {/* {!isSidebarCollapsed ? ( */}
              {/* <> */}
              <Link
                to="/"
                className="block hover:opacity-80 transition-opacity"
              >
                <img
                  src="/imgs/logos/gray-logo.png"
                  alt="Alayna AI Logo"
                  className="w-8 h-8"
                />
              </Link>
              {/* COMMENTED OUT: Collapse button disabled
                  <button
                    onClick={() => setIsSidebarCollapsed(true)}
                    className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-200 transition-colors"
                    aria-label="Collapse sidebar"
                  >
                    <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
                  </button>
                  */}
              {/* </> */}
              {/* ) : (
                <div className="w-full flex justify-center">
                  {isHoveringSidebar ? (
                    <button
                      onClick={() => setIsSidebarCollapsed(false)}
                      className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-200 transition-colors"
                      aria-label="Expand sidebar"
                    >
                      <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  ) : (
                    <Link
                      to="/"
                      className="block hover:opacity-80 transition-opacity"
                    >
                      <img
                        src="/imgs/logos/gray-logo.png"
                        alt="Alayna AI Logo"
                        className="w-8 h-8"
                      />
                    </Link>
                  )}
                </div>
              )} */}
            </div>

            {/* COMMENTED OUT: Conditional rendering for collapsed sidebar disabled */}
            {/* {!isSidebarCollapsed ? ( */}
            {/* <> */}
            {/* Primary Navigation */}
            <div className="p-4 space-y-1">
              <Link
                to="/"
                className={clsx(
                  "flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                  isHomeActive
                    ? "bg-[#eaeaea] text-gray-900"
                    : "text-gray-700 hover:bg-gray-50"
                )}
              >
                <HomeIcon className="w-4 h-4 text-black" />
                Home
              </Link>
              {/* {!!myDriveId && (
            <Link
              to={driveRoute(myDriveId)}
              className={clsx(
                    "flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors",
                isWorkspaceActive
                  ? "bg-[#eaeaea] text-gray-900"
                  : "text-gray-700 hover:bg-gray-50"
              )}
            >
                  <FolderIcon className="w-4 h-4 text-black" />
              My Workspace
            </Link>
          )} */}
            </div>

            {/* Continue working Section */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto px-4 pb-4"
            >
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-2">
                Continue working
              </h3>
              <div className="space-y-1">
                {auth.isFetchingRecentActivities ? (
                  <div className="space-y-2 px-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse" />
                        <div className="flex-1">
                          <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse mb-1" />
                          <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : auth.errorFetchingRecentActivities ? (
                  <div className="px-2 py-4 text-sm text-red-600 text-center">
                    Failed to load activities
                  </div>
                ) : recentActivities.length === 0 ? (
                  <div className="px-2 py-4 text-sm text-gray-500 text-center">
                    No recent activities
                  </div>
                ) : (
                  <>
                    {recentActivities.map((activity) => (
                      <ActivityListItem key={activity.id} activity={activity} />
                    ))}
                    {/* Sentinel for infinite scroll */}
                    {auth.hasMoreRecentActivities && (
                      <div ref={sentinelRef} className="py-2">
                        {auth.isFetchingMoreRecentActivities && (
                          <div className="flex items-center justify-center gap-2 text-gray-400 text-xs">
                            <div className="w-3 h-3 border border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                            Loading more...
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Footer - Navbar footer */}
            <div className="p-4 border-t border-gray-200 space-y-1">
              {renderManageSubscriptionButton()}
              <a
                href="https://www.canva.com/design/DAF5XKzIa_o/PLPm0juXs0bBBT_LQCBSSQ/edit?utm_content=DAF5XKzIa_o&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <PresentationIcon className="w-4 h-4 text-black" />
                PD Deck
              </a>
              <a
                href="mailto:hello@alayna.ai?subject=Bring Alayna to Your School"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MailIcon className="w-4 h-4 text-black" />
                Bring Alayna to Your School
              </a>

              {/* Profile Dropdown */}
              <AccountDropdown>
                <button className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  {auth.user.avatarUrl ? (
                    <img
                      src={auth.user.avatarUrl}
                      alt="User Avatar"
                      className="w-7 h-7 rounded-full"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium text-xs">
                      {auth.user?.firstName?.charAt(0)}
                      {auth.user?.lastName?.charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {auth.user.firstName} {auth.user.lastName}
                    </p>
                    {currentPlan !== PricingPlanTier.Basic && (
                      <p className="text-xs text-pink-600">PRO</p>
                    )}
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                </button>
              </AccountDropdown>
            </div>
            {/* </> */}
            {/* COMMENTED OUT: Collapsed sidebar rendering disabled for desktop
            ) : (
              <>
                {/* Collapsed Navigation */}
            {/* <div className="flex-1 flex flex-col items-center py-4 space-y-2">
                  <Tooltip content="Home" side="right" delayDuration={300}>
                    <Link
                      to="/"
                      className={clsx(
                        "flex items-center justify-center w-10 h-10 rounded-lg transition-colors",
                        isHomeActive
                          ? "bg-[#eaeaea] text-gray-900"
                          : "text-gray-700 hover:bg-gray-50"
                      )}
                    >
                      <HomeIcon className="w-4 h-4 text-black" />
                    </Link>
                  </Tooltip>

                  {/* {!!myDriveId && (
                <Tooltip content="My Workspace" side="right" delayDuration={300}>
                  <Link
                    to={driveRoute(myDriveId)}
                    className={clsx(
                      "flex items-center justify-center w-10 h-10 rounded-lg transition-colors",
                      isWorkspaceActive
                        ? "bg-[#eaeaea] text-gray-900"
                        : "text-gray-700 hover:bg-gray-50"
                    )}
                  >
                    <FolderIcon className="w-4 h-4 text-black" />
                  </Link>
                </Tooltip>
              )} */}
            {/* </div> */}

            {/* Collapsed Footer */}
            {/* <div className="p-4 border-t border-gray-200 flex flex-col items-center space-y-2">
                  <Tooltip
                    content="PD Deck"
                    side="right"
                    align="start"
                    delayDuration={300}
                  >
                    <a
                      href="https://www.canva.com/design/DAF5XKzIa_o/PLPm0juXs0bBBT_LQCBSSQ/edit?utm_content=DAF5XKzIa_o&utm_campaign=designshare&utm_medium=link2&utm_source=sharebutton"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <PresentationIcon className="w-4 h-4 text-black" />
                    </a>
                  </Tooltip>

                  <Tooltip
                    content="Bring Alayna to Your School"
                    side="right"
                    align="start"
                    delayDuration={300}
                  >
                    <a
                      href="mailto:hello@alayna.ai?subject=Bring Alayna to Your School"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <MailIcon className="w-4 h-4 text-black" />
                    </a>
                  </Tooltip>

                  {/* Collapsed Profile */}
            {/* <div className="pt-2">
                    <AccountDropdown>
                      <button className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-50 transition-colors">
                        {auth.user.avatarUrl ? (
                          <img
                            src={auth.user.avatarUrl}
                            alt="User Avatar"
                            className="w-8 h-8 rounded-full"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-medium text-xs">
                            {auth.user?.firstName?.charAt(0)}
                            {auth.user?.lastName?.charAt(0)}
                          </div>
                        )}
                      </button>
                    </AccountDropdown>
                  </div>
                </div>
              </>
            )} */}
          </div>
        )}

        {/* Main Workspace Area */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="flex justify-center">
            <div className={clsx("w-full", isMobile ? "p-4" : "max-w-4xl p-8")}>
              {/* Section: Ask Alayna */}
              <div className={clsx("mb-12", isMobile && "mb-8")}>
                <h1
                  className={clsx(
                    "font-bold text-gray-900 mb-6 mt-6",
                    isMobile ? "text-2xl" : "text-3xl"
                  )}
                >
                  {greetingMessage}
                </h1>

                {/* Mode Selector */}
                <div className="inline-flex p-1.5 bg-gray-100 rounded-2xl mb-6 border border-gray-200 shadow-sm">
                  <button
                    onClick={() => {
                      // setSelectedMode("chat");
                      // navigate("/chat");
                    }}
                    className={clsx(
                      "relative flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200",
                      selectedMode === "chat"
                        ? "bg-gray-900 text-white shadow-md"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <ChatIcon
                      className={clsx(
                        "w-4 h-4 transition-colors",
                        selectedMode === "chat" ? "text-white" : "text-gray-500"
                      )}
                    />
                    Chat
                  </button>
                  <button
                    onClick={() => {
                      setSelectedMode("prompts");
                      navigate(tasksRoute());
                    }}
                    className={clsx(
                      "relative flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200",
                      selectedMode === "prompts"
                        ? "bg-gray-900 text-white shadow-md"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <AppsaddIcon
                      className={clsx(
                        "w-4 h-4 transition-colors",
                        selectedMode === "prompts"
                          ? "text-white"
                          : "text-gray-500"
                      )}
                    />
                    Common Tasks
                  </button>
                  <button
                    onClick={() => {
                      setSelectedMode("image");
                      navigate("/image-generator");
                    }}
                    className={clsx(
                      "relative flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200",
                      selectedMode === "image"
                        ? "bg-gray-900 text-white shadow-md"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    )}
                  >
                    <ImageIcon
                      className={clsx(
                        "w-4 h-4 transition-colors",
                        selectedMode === "image"
                          ? "text-white"
                          : "text-gray-500"
                      )}
                    />
                    Image
                  </button>
                </div>

                {/* Chat Input Box - Functional */}
                <div className="max-w-4xl">
                  <HomeChatInput
                    text={text}
                    onTextChange={handleTextChange}
                    onSend={async (args?: {
                      query?: { prompt?: string };
                    }) => {
                      const { prompt } = args?.query || {};

                      if (!auth.user) return;

                      const formattedText = extractTextFromHtml(text);

                      // Use prompt if provided, otherwise use formatted text from state (matches Chat.tsx behavior)
                      const finalText =
                        prompt && prompt !== "" ? prompt : formattedText;
                      if (!finalText || finalText.trim() === "") return;

                      try {
                        const res = await conversations.createNewConversation({
                          user: {
                            id: auth.user.id,
                          },
                          entry: {
                            text: finalText,
                            assetIds:
                              assetIds.length > 0 ? assetIds : undefined,
                          },
                        });

                        await conversations.fetchByUrlId(res.urlId);

                        conversationParticipants.refreshUserBotConversationParticipants();

                        if (auth.user.referralActionsPending?.useChat) {
                          auth.completedReferralActionChat(
                            EnumReferralPendingAction.ChatUse
                          );
                        }

                        setText("");
                        auth.incrementChatCount();

                        mixpanel.track("new-conversation-initiated", {
                          text: finalText,
                          chatId: res.urlId,
                          assetIds,
                        });

                        // Navigate to the real chat, passing assetIds so they persist
                        const currentAssetIds = [...assetIds];
                        setAssetIds([]);
                        navigate(conversationRoute(res.urlId), {
                          state: { assetIds: currentAssetIds },
                        });
                      } catch (error) {
                        console.error("Failed to create conversation", error);
                        // Plan-limit rejections open the ProPlanWall via the
                        // global bridge; a toast on top would be noise.
                        if (!isPlanLimitError(error)) {
                          addToast("Failed to create conversation", {
                            type: "error",
                          });
                        }
                      }
                    }}
                    assetIds={assetIds}
                    setAssetIds={setAssetIds}
                    chatFileUploadUsesLeft={chatFileUploadUsesLeft}
                    currentPlan={currentPlan}
                  />
                </div>
              </div>

              <GradingPalHomeBanner />

              {/* Section: Other Alayna AI tools */}
              <div className={clsx("mt-16", isMobile && "mt-8")}>
                <p
                  className={clsx(
                    "text-center font-medium text-gray-600 uppercase tracking-wider",
                    isMobile ? "text-xs mb-4" : "text-sm mb-6"
                  )}
                >
                  Or Select an App to Get Started
                </p>

                <div
                  className={clsx(
                    "grid gap-6",
                    isMobile ? "grid-cols-1" : "grid-cols-3"
                  )}
                >
                  {/* Magic Materials */}
                  <Link
                    to="/instant-resources"
                    className="group relative rounded-2xl p-6 bg-white border border-gray-200 shadow-lg shadow-gray-100/50 hover:shadow-lg hover:shadow-gray-200/50 transition-all hover:-translate-y-1"
                  >
                    <div className="inline-flex p-3 rounded-xl bg-violet-500 mb-4">
                      <AppsaddIcon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-black mb-2 group-hover:text-blue-600 transition-colors">
                      Magic Materials
                    </h3>
                    <p className="text-black text-sm leading-relaxed">
                      One click. 20+ teaching resources instantly
                    </p>
                    <svg
                      className="absolute bottom-6 right-6 w-5 h-5 text-black group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>

                  {/* Slides Generator */}
                  <Link
                    to="/slides"
                    className="group relative rounded-2xl p-6 bg-white border border-gray-200 shadow-lg shadow-gray-100/50 hover:shadow-lg hover:shadow-gray-200/50 transition-all hover:-translate-y-1"
                  >
                    <div className="inline-flex p-3 rounded-xl bg-orange-500 mb-4">
                      <MonitorIcon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-black mb-2 group-hover:text-blue-600 transition-colors">
                      Slides Generator
                    </h3>
                    <p className="text-black text-sm leading-relaxed">
                      Transform ideas into polished presentations in seconds
                    </p>
                    <svg
                      className="absolute bottom-6 right-6 w-5 h-5 text-black group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>

                  {/* GradingPal */}
                  <Link
                    to="/grading"
                    className="group relative rounded-2xl p-6 bg-white border border-gray-200 shadow-lg shadow-gray-100/50 hover:shadow-lg hover:shadow-gray-200/50 transition-all hover:-translate-y-1"
                  >
                    <div className="inline-flex p-3 rounded-xl bg-green-500 mb-4">
                      <CheckmarkcircleIcon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-black mb-2 group-hover:text-blue-600 transition-colors">
                      GradingPal
                    </h3>
                    <p className="text-black text-sm leading-relaxed">
                      Upload an assignment. Start grading here.
                    </p>
                    <svg
                      className="absolute bottom-6 right-6 w-5 h-5 text-black group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(Home);
